import {
  TAG_CATEGORIES,
  TAG_CATEGORY_INFO,
  TAG_EMPHASES,
  type TagCategory,
  type TagEmphasis,
} from '@/semantics/SemanticTag';
import { frameBrief, type MapFrame } from '@/geo/MapFrame';
import {
  DEFAULT_REFERENCE_TAG_RADIUS,
  createReferenceTag,
  type ReferenceTag,
} from './ReferenceTag';
import { SitePlanFormatError } from './SitePlan';
import {
  callVisionModel,
  extractJson,
  type ReferencePrompt,
  type VisionCallResult,
} from './SitePlanClient';

/**
 * The pre-pass that tags the photograph before the designer does.
 *
 * ## What it is for, and what it is not for
 *
 * Tagging is the highest-value input the pipeline takes and the one most likely to be skipped,
 * because it is the only step that is pure typing. Nothing about it was ever hard — it was *blank*,
 * and a blank panel with a photograph in it is an invitation to press Run and hope.
 *
 * So this fills the panel in. One cheap call comes back with a dozen labelled circles — the drive,
 * the bin store, the neighbour's garage, the bed that is actually a paddock — and the designer's job
 * becomes correcting a draft instead of composing one. That is a far easier task to start, and
 * correcting is where the real knowledge enters: the model can see a shed, and only a person knows
 * it is coming down in March.
 *
 * It is emphatically **not** a substitute for the designer. Everything it returns is marked
 * `origin: 'suggested'` and stays marked until someone edits it, and `referenceTagBrief` quotes
 * suggestions in their own section under an explicit warning that nobody has confirmed them. A model
 * reading its own earlier guess back as "the designer, who knows this site, says" is precisely how a
 * confident wrong answer gets laundered into a fact, and it is the one failure this feature exists
 * to prevent.
 *
 * ## Why it is a separate call rather than part of stage 1
 *
 * Stage 1 already reads the photograph, so asking it to also emit tags looks like a saving. It is
 * not: the tags have to exist *before* stage 1 runs, because their entire purpose is to be in stage
 * 1's prompt. A suggestion produced by stage 1 could only inform stages 2–5, which is the half of
 * the pipeline that was never the problem.
 *
 * The call is deliberately small — one image, a short schema, a low ceiling on output — because it
 * is offered as a free-feeling convenience and priced accordingly. It also runs on its *own*
 * default model rather than whatever the reconstruction dialog happens to be set to: it used to
 * inherit that dropdown, which quietly ran the cheapest call in the pipeline on the flagship. A
 * caller may still name a model, and none of them do.
 */

/** Never accept more than this from one reply. A photograph has a legible number of things in it. */
export const MAX_SUGGESTED_TAGS = 24;

/** What the prompt asks for. Below the ceiling, so a model aiming high still lands in range. */
const TARGET_TAG_COUNT = 14;

/** Enough for a couple of dozen short entries and no more. */
const SUGGEST_OUTPUT_TOKENS = 4000;

export interface SuggestTagsOptions {
  /** How many images accompany the request, so the prompt can name which one to tag. */
  imageCount?: number;
  /** The direction box, which usually already says what the place is. */
  direction?: string;
  /** Present when the reference came from the map, which changes the view radically. */
  frame?: MapFrame;
}

export interface SuggestTagsRequest extends SuggestTagsOptions {
  apiKey: string;
  /** The photograph being tagged. The first reference image — the one the tagger draws on. */
  images: readonly Blob[];
  model?: string;
  signal?: AbortSignal;
  /** Injected for tests. Defaults to the real vision call. */
  call?: typeof callVisionModel;
  /** Injected so suggested tags have stable timestamps in tests. */
  now?: Date;
}

export interface SuggestTagsResult {
  tags: ReferenceTag[];
  /** Whatever the model wanted to say about its own reading. Shown to the user, never to a stage. */
  notes?: string;
  model: string;
  /** The raw reply, kept for the same reason every stage keeps one. */
  raw: string;
  usage?: { promptTokens: number; completionTokens: number };
}

export async function suggestReferenceTags(request: SuggestTagsRequest): Promise<SuggestTagsResult> {
  const prompt = buildTagSuggestionPrompt({
    ...(request.imageCount !== undefined ? { imageCount: request.imageCount } : {}),
    ...(request.direction !== undefined ? { direction: request.direction } : {}),
    ...(request.frame ? { frame: request.frame } : {}),
  });

  const call: VisionCallResult = await (request.call ?? callVisionModel)({
    apiKey: request.apiKey,
    operation: 'tag-suggest',
    prompt,
    images: request.images,
    maxOutputTokens: SUGGEST_OUTPUT_TOKENS,
    ...(request.model !== undefined ? { model: request.model } : {}),
    ...(request.signal ? { signal: request.signal } : {}),
  });

  const parsed = parseSuggestedTags(JSON.parse(extractJson(call.raw)), request.now);

  return {
    tags: parsed.tags,
    ...(parsed.notes !== undefined ? { notes: parsed.notes } : {}),
    model: call.model,
    raw: call.raw,
    ...(call.usage ? { usage: call.usage } : {}),
  };
}

/**
 * Read the reply into tags.
 *
 * Forgiving in exactly the way the rest of this feature is: a bad *entry* is dropped, a bad
 * *structure* throws. One tag with a missing label costs nothing to skip; a reply that is not a list
 * of tags at all means the call failed and saying so beats presenting an empty panel as a result.
 */
export function parseSuggestedTags(
  raw: unknown,
  now?: Date,
): { tags: ReferenceTag[]; notes?: string } {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    throw new SitePlanFormatError('Expected an object with a "tags" list.');
  }
  const data = raw as Record<string, unknown>;
  if (!Array.isArray(data.tags)) {
    throw new SitePlanFormatError('Expected "tags" to be a list.');
  }

  const tags: ReferenceTag[] = [];
  for (const entry of data.tags) {
    if (typeof entry !== 'object' || entry === null) continue;
    const record = entry as Record<string, unknown>;

    const label = typeof record.label === 'string' ? record.label.trim() : '';
    if (label.length === 0) continue;

    const u = Number(record.u);
    const v = Number(record.v);
    if (!Number.isFinite(u) || !Number.isFinite(v)) continue;

    const category = TAG_CATEGORIES.includes(record.category as TagCategory)
      ? (record.category as TagCategory)
      : 'identity';
    const emphasis = TAG_EMPHASES.includes(record.emphasis as TagEmphasis)
      ? (record.emphasis as TagEmphasis)
      : 'mention';
    const note = typeof record.note === 'string' && record.note.trim().length > 0
      ? record.note
      : undefined;

    tags.push(
      createReferenceTag({
        // `createReferenceTag` clamps every number into range, so a model that answers in percent or
        // wanders outside the frame produces a tag in the wrong place rather than a broken one.
        target: {
          u,
          v,
          radius: Number.isFinite(Number(record.radius))
            ? Number(record.radius)
            : DEFAULT_REFERENCE_TAG_RADIUS,
        },
        label,
        category,
        emphasis,
        origin: 'suggested',
        ...(note !== undefined ? { note } : {}),
        ...(now ? { now } : {}),
      }),
    );
    if (tags.length >= MAX_SUGGESTED_TAGS) break;
  }

  const notes = typeof data.notes === 'string' && data.notes.trim().length > 0
    ? data.notes.trim()
    : undefined;
  return { tags, ...(notes !== undefined ? { notes } : {}) };
}

/* ----------------------------------- Prompt ----------------------------------- */

/**
 * The method is the system message and nothing else, so it is byte-identical on every call.
 *
 * The map-frame brief used to sit inside it, between the task and the schema, which put a paragraph
 * that changes with every capture in the middle of ~3,900 characters that never change — and a
 * cached prefix ends at the first byte that differs. It is a fact about *this* photograph, so it
 * belongs with the photograph. See `shared/ai/caching.ts`.
 */
export function buildTagSuggestionPrompt(options: SuggestTagsOptions = {}): ReferencePrompt {
  return {
    system: [ROLE, categorySection(), TASK, SCHEMA].join('\n\n'),
    user: userMessage(options),
  };
}

const ROLE = `You are making a first pass at annotating a photograph of a real garden or property,
so that a designer can correct your annotations rather than start from a blank panel.

You mark PLACES. Each annotation is a circle on the image and a short phrase saying what is there.
You are not reconstructing the site, sizing anything, or planning a design — later stages do all of
that, and they will read what you mark here.

Be accurate before you are thorough. A wrong label placed confidently is worse than a missing one,
because it is about to be shown to a person as a starting point and to five later stages as a
reading of the pixels. Where you are unsure, say so in the tag's "note" rather than guessing
silently.`;

/**
 * The six categories, listed from the tag layer itself.
 *
 * Read from `TAG_CATEGORY_INFO` rather than restated so the vocabulary a suggestion arrives in is
 * exactly the vocabulary the panel offers for editing it — a suggested category the picker does not
 * have would be an edit the designer cannot make.
 */
function categorySection(): string {
  const lines = TAG_CATEGORY_INFO.map(
    (info) => `- "${info.id}" — ${info.name}. ${info.hint} e.g. “${info.example}”`,
  ).join('\n');
  return `# The six kinds of statement\n\n${lines}`;
}

const TASK = `# What to mark

Aim for about ${TARGET_TAG_COUNT} annotations, and never more than ${MAX_SUGGESTED_TAGS}. Spread them
over the whole frame rather than clustering them on the most photogenic corner.

1. NAME WHAT WOULD BE MISREAD. This is the most valuable thing you can do here. A gravel rectangle
   with a bin on it is a bin store, not a fire-pit lounge; a rough field is a paddock, not a lawn; a
   roof at the edge of the frame usually belongs to a neighbour. These are "identity" tags and they
   should be most of what you return.

2. MARK WHAT IS NOT PART OF THIS SITE with the "avoid" category — a neighbour's garage, the public
   road, a car that happens to be parked there. Later stages will otherwise build them.

3. MARK SURFACES AND CONDITION where a photograph shows something a generic reading would flatten:
   "self-binding gravel, not pea shingle", "block paving, moss in the joints".

4. USE THE REACH HONESTLY. "radius" is a fraction of the image WIDTH — roughly 0.03 for a door or a
   bin, 0.08 for a shed or a car, 0.2 for a lawn or a whole drive. A tag whose circle covers half the
   picture says nothing about where the thing is.

5. EMPHASIS is "mention" unless you have a reason. Use "critical" only for something the whole site
   would be wrong without.

Do NOT mark: individual plants you cannot identify, things you are inferring rather than seeing, or
the site as a whole. There is no whole-photograph annotation — every tag is somewhere you could put
a finger on. General statements about the place belong to the designer's direction box, not here.`;

const SCHEMA = `# Output schema

Return one JSON object and nothing else — no prose, no markdown fence:

{
  "tags": [
    {
      "u": 0.42, "v": 0.61, "radius": 0.05,
      "label": "bin store",
      "category": "identity",
      "emphasis": "mention",
      "note": "three wheelie bins behind a slatted screen — could be read as a seating area"
    }
  ],
  "notes": "what you were unsure of, and anything you deliberately did not mark"
}

- "u" and "v" are normalized image coordinates in [0, 1]: origin at the TOP-LEFT, u increasing
  right, v increasing DOWN. They are fractions, not pixels and not percentages.
- "label" is a short phrase, at most a few words. Detail goes in "note".
- Every "category" is one of the six ids above; every "emphasis" is mention | emphasise | critical.`;

function userMessage(options: SuggestTagsOptions): string {
  const count = options.imageCount ?? 1;
  const parts = [
    ...(options.frame ? [frameBrief(options.frame)] : []),
    count === 1
      ? 'Annotate the attached photograph.'
      : `Annotate the FIRST of the ${count} attached photographs — the others are the same site from ` +
        'other angles and are context only. Every coordinate you return refers to the first image.',
  ];

  const direction = options.direction?.trim();
  if (direction) {
    // The designer has usually already typed what the place is; a pre-pass that ignores it would ask
    // them to say it twice and would contradict them on the half it guessed.
    parts.push(
      'The designer has said this about the site. It comes from someone who has been there, so it ' +
        'overrides your reading of the pixels wherever the two disagree:\n' +
        direction,
    );
  }

  return parts.join('\n\n');
}
